import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { combineLatest, debounceTime, Subject } from 'rxjs';
import { IModule } from 'src/app/interfaces/IModule';
import { DepartmentalQuestionnairesService } from 'src/app/services/departmentalQuestionnaires.service';
import { paths } from 'src/component-paths/component-paths';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import { PatientService } from 'src/app/services/patient.service';
import { Router } from '@angular/router';
import { QuestionnaireHistoryItem } from 'src/app/interfaces/Questionnaire/IQuestionnaireHistory';
import { getQuestionnaireTemplateConfigByPath } from 'src/app/constants/questionnaire-template-registry';

@Component({
  selector: 'app-departmental-questionnaires-section',
  templateUrl: './departmental-questionnaires-section.component.html',
  styleUrl: './departmental-questionnaires-section.component.css',
  standalone: false,
})
export class DepartmentalQuestionnairesSectionComponent implements OnChanges {
  @Input() submenu?: IModule[];
  @Input() overviewItemsByTemplateIdInput: Record<
    number,
    QuestionnaireHistoryItem[]
  > | null = null;
  @Input() overviewLoadingInput: boolean | null = null;
  @Input() overviewErrorInput: string | null = null;
  componentPaths = paths;
  isLoading: boolean = false;
  localOverviewLoading = false;
  localOverviewError = '';
  localOverviewItemsByTemplateId: Record<number, QuestionnaireHistoryItem[]> =
    {};
  @Input() selectedOption?: string;
  @Output() selectedOptionChange = new EventEmitter<string>();
  @Input() readonlyMode = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['submenu'] ||
        changes['readonlyMode'] ||
        changes['selectedOption']) &&
      !this.usesExternalOverview()
    ) {
      this.loadOverview();
    }
  }

  private scrollSubject = new Subject<Event>();

  constructor(
    private readonly departmentalQuestionnairesService: DepartmentalQuestionnairesService,
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly router: Router,
  ) {}

  ngAfterViewInit() {
    setTimeout(() => {
      combineLatest([
        this.departmentalQuestionnairesService
          .departmentalQuestionnairesLoadingSub$,
      ]).subscribe(([value1]) => {
        const sectionPath = this.submenu?.find(
          (sm) => sm.path == this.selectedOption,
        )?.path;
        if (!value1 && sectionPath) {
          const element = document.getElementById(sectionPath);
          if (element) {
            // menu, patient header, title, margin
            const offset = element.offsetTop - 120 - 66 - 64 - 16;
            window.scroll({
              top: offset,
              behavior: 'smooth',
            });

            this.scrollSubject
              .pipe(
                debounceTime(100), // Adjust debounce time as needed (in milliseconds)
              )
              .subscribe(() => {
                // Your existing scroll logic here
                const stQ = document.getElementById(
                  paths.speechTherapyQuestionnaire,
                );
                if (stQ) {
                  const rect = stQ.getBoundingClientRect();
                  if (rect.top >= 120) {
                    this.selectedOption = this.submenu?.find(
                      (sm) => sm.path == paths.speechTherapyQuestionnaire,
                    )?.path;
                    this.selectedOptionChange.emit(this.selectedOption);
                  }
                }
              });
          }
        }
      });
    }, 500);
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  handleOverviewRefreshRequested(): void {
    this.loadOverview();
  }

  get isOverviewLoading(): boolean {
    return this.overviewLoadingInput ?? this.localOverviewLoading;
  }

  get overviewError(): string {
    return this.overviewErrorInput ?? this.localOverviewError;
  }

  get overviewItemsByTemplateId(): Record<number, QuestionnaireHistoryItem[]> {
    return (
      this.overviewItemsByTemplateIdInput ??
      this.localOverviewItemsByTemplateId
    );
  }

  get activeDepartmentModule(): IModule | undefined {
    return this.resolveActiveDepartmentModule();
  }

  private loadOverview(): void {
    if (this.usesExternalOverview()) {
      return;
    }

    const patientId = this.patientService.patientIDsignal();
    const templateIds = this.collectTemplateIds(
      this.activeDepartmentModule?.childModules,
    );

    if (!patientId || !templateIds.length) {
      this.localOverviewItemsByTemplateId = {};
      return;
    }

    this.localOverviewLoading = true;
    this.localOverviewError = '';

    this.questionnaireService
      .getQuestionnaireOverview(patientId, {
        templateIds,
        ...(this.isVisitMode()
          ? {
              visitId: this.eocService.currentVisitId() || undefined,
              episodeCareId: this.eocService.currentEocId() || undefined,
              includeDrafts: true,
            }
          : {}),
        section: paths.departmentalQuestionnaires,
        forceRefresh: true,
      })
      .subscribe({
        next: (response) => {
          this.localOverviewItemsByTemplateId = response.templates.reduce(
            (accumulator: Record<number, QuestionnaireHistoryItem[]>, template) => {
              accumulator[template.questionnaireTemplateId] = template.items ?? [];
              return accumulator;
            },
            {},
          );
          this.localOverviewLoading = false;
        },
        error: () => {
          this.localOverviewItemsByTemplateId = {};
          this.localOverviewError = 'Could not load questionnaire overview.';
          this.localOverviewLoading = false;
        },
      });
  }

  private isVisitMode(): boolean {
    return (
      !!this.eocService.currentVisitId() &&
      (this.router.url.includes('episodeOfCare') ||
        this.router.url.includes('inPatientView'))
    );
  }

  private collectTemplateIds(modules?: IModule[]): number[] {
    return Array.from(
      new Set(
        modules
          ?.flatMap((module: IModule) => [
            getQuestionnaireTemplateConfigByPath(module.path)?.id,
            ...this.collectTemplateIds(module.childModules),
          ])
          .filter((id: number | undefined): id is number => !!id) ?? [],
      ),
    );
  }

  private resolveActiveDepartmentModule(): IModule | undefined {
    if (!this.submenu?.length) {
      return undefined;
    }

    const selected = this.selectedOption;

    if (!selected || selected === paths.departmentalQuestionnaires) {
      return this.submenu[0];
    }

    return (
      this.submenu.find((module: IModule) =>
        this.moduleContainsPath(module, selected),
      ) ?? this.submenu[0]
    );
  }

  private moduleContainsPath(module: IModule, selected: string): boolean {
    if (module.path === selected) {
      return true;
    }

    return (
      module.childModules?.some((child: IModule) =>
        this.moduleContainsPath(child, selected),
      ) ?? false
    );
  }

  private usesExternalOverview(): boolean {
    return this.overviewItemsByTemplateIdInput !== null;
  }
}
